import React, { useMemo, useState } from "react";
import {
  FaInstagram,
  FaFacebookF,
  FaTiktok,
  FaWhatsapp,
  FaMapMarkedAlt,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { getSessionId } from "../../utils";
import { registrarAcao } from "../../api/public";
import {
  buildLinkHref,
  getActionTypeByLinkType,
  getLinksByType,
  isUuid,
} from "../../utils/storeLinks";
import LinkChoiceModal from "./LinkChoiceModal";
import {
  DEFAULT_STORE_PROFILE_PICTURE,
  getStoreProfilePictureUrl,
} from "../../utils/storeProfilePicture";

const DEFAULT_WHATSAPP_MESSAGE =
  "Ola! Vi sua loja na Vintree e gostaria de saber mais sobre seus produtos.";

function HeaderCatalogo({ loja }) {
  const sessionId = getSessionId();
  const [pickerState, setPickerState] = useState({
    open: false,
    title: "",
    links: [],
    whatsappText: null,
  });

  const socialConfig = useMemo(
    () => [
      {
        tipo: "whatsapp",
        title: "Escolha um WhatsApp",
        Icon: FaWhatsapp,
        whatsappText: DEFAULT_WHATSAPP_MESSAGE,
      },
      {
        tipo: "instagram",
        title: "Escolha um Instagram",
        Icon: FaInstagram,
      },
      {
        tipo: "facebook",
        title: "Escolha um Facebook",
        Icon: FaFacebookF,
      },
      {
        tipo: "tiktok",
        title: "Escolha um TikTok",
        Icon: FaTiktok,
      },
      {
        tipo: "twitter_x",
        title: "Escolha um perfil no X",
        Icon: FaXTwitter,
      },
      {
        tipo: "geolocalizacao",
        title: "Escolha um endereco",
        Icon: FaMapMarkedAlt,
      },
    ],
    []
  );

  if (!loja) return null;

  const trackLinkClick = (link, origem = "header") => {
    const payload = {
      session_id: sessionId,
      loja_id: loja.id,
      tipo_acao: getActionTypeByLinkType(link?.tipo),
      origem,
      link_tipo: link?.tipo || null,
      link_label: link?.label || null,
      link_valor: link?.valor || null,
    };

    if (isUuid(link?.id)) {
      payload.loja_link_id = link.id;
    }

    registrarAcao(payload).catch((err) =>
      console.error("Erro ao registrar acao de link:", err)
    );
  };

  const openPicker = ({ title, links, whatsappText = null }) => {
    setPickerState({
      open: true,
      title,
      links,
      whatsappText,
    });
  };

  const closePicker = () => {
    setPickerState({ open: false, title: "", links: [], whatsappText: null });
  };

  const renderSocialAction = ({ tipo, title, Icon, whatsappText = null }) => {
    const links = getLinksByType(loja, tipo);
    if (!links.length) return null;

    if (links.length === 1) {
      const link = links[0];
      const href = buildLinkHref(link, { whatsappText });
      if (!href) return null;

      return (
        <a
          key={`${tipo}-${link.id}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackLinkClick(link, "header")}
        >
          <Icon className="text-gray-600 text-3xl hover:text-black transition-all" />
        </a>
      );
    }

    return (
      <button
        key={`${tipo}-multi`}
        type="button"
        onClick={() => openPicker({ title, links, whatsappText })}
      >
        <Icon className="text-gray-600 text-3xl hover:text-black transition-all" />
      </button>
    );
  };

  return (
    <>
      <header className="bg-white p-6 max-w-4xl mx-auto flex flex-col md:items-center gap-6">
        <div className="flex justify-center m-6">
          <img
            src={getStoreProfilePictureUrl(
              loja.profile_picture,
              loja.updatedAt || loja.updated_at
            )}
            onError={(event) => {
              if (event.currentTarget.src.endsWith(DEFAULT_STORE_PROFILE_PICTURE)) {
                return;
              }
              event.currentTarget.src = DEFAULT_STORE_PROFILE_PICTURE;
            }}
            alt={`Foto de perfil da loja ${loja.nome_fantasia}`}
            className="h-24 w-24 rounded-full object-cover border"
          />
        </div>

        <div className="text-center">
          <h1 className="text-3xl font-extrabold text-gray-900">
            {loja.nome_fantasia}
          </h1>
          {loja?.slogan && <p className="text-lg text-gray-700">{loja.slogan}</p>}

          <div className="flex justify-center gap-4 mt-4">
            {socialConfig.map(renderSocialAction)}
          </div>
        </div>
      </header>

      <LinkChoiceModal
        open={pickerState.open}
        onClose={closePicker}
        title={pickerState.title}
        links={pickerState.links}
        onSelect={(link) => {
          const href = buildLinkHref(link, {
            whatsappText: pickerState.whatsappText,
          });

          if (!href) return;

          trackLinkClick(link, "header");
          window.open(href, "_blank", "noopener,noreferrer");
          closePicker();
        }}
      />
    </>
  );
}

export default HeaderCatalogo;
